/**
 * Onboarding-health dashboard — query hook.
 *
 * Backed by GET /api/v1/tenants/{tenant_id}/onboarding-health.
 * The path-tenant rewriter in api-client maps this to
 * /api/tenant/v1/onboarding-health when the SPA runs on a tenant
 * host, so a single query hook serves both platform-admin and
 * tenant-admin contexts.
 */

import { useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  onboardingHealthStatusSchema,
  type OnboardingHealthStatus,
} from "./schemas";

export async function fetchOnboardingHealth(
  tenantID: string,
): Promise<OnboardingHealthStatus> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/onboarding-health`,
  );
  return onboardingHealthStatusSchema.parse(raw);
}

export function useOnboardingHealthQuery(tenantID: string) {
  return useQuery<OnboardingHealthStatus, ApiError>({
    queryKey: queryKeys.onboardingHealth(tenantID),
    queryFn: () => fetchOnboardingHealth(tenantID),
    enabled: tenantID.length > 0,
    // 60s stale gives the dashboard breathing room while
    // letting a tenant admin who hits "refresh" see fresh data
    // without a forced cache bust. Audit + notification queries
    // are cheap (single-tenant indexed lookups) so a shorter
    // window costs little even if the page renders once a
    // minute.
    staleTime: 60_000,
    retry: false,
  });
}
